import Autocomplete from '@material-ui/lab/Autocomplete';
import { createFilterOptions } from '@material-ui/lab/useAutocomplete';
import { Opt } from 'app/tools/general.types';
import _, { isEqual } from 'lodash';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldValues,
  UseControllerOptions,
} from 'react-hook-form';
import styled, { css } from 'styled-components/macro';
import FormTextInput from './FormTextInput';
import { InputPropsFor } from './utilities/input.types';

const CreatedOptSym = Symbol('indicates a custom-created option');
export type CreatedOpt = {
  inputValue: string;
  title: string;
  [CreatedOptSym]: true;
};

type ACTextInputOpts = { $multiple: Opt<boolean> };
const AutocompleteTextInput = styled(FormTextInput)<ACTextInputOpts>`
  ${p =>
    p.$multiple &&
    css`
      .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
        padding-top: 9px;
        padding-bottom: 9px;
      }
    `}
`;

type StyledACOpts = { disableClearIcon: Opt<boolean> };
const useAutocompleteCss = (p: StyledACOpts) =>
  useMemo(
    () => css`
      ${p.disableClearIcon &&
      css`
        &.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon
          .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
          padding-right: 39px;
        }
        .MuiAutocomplete-clearIndicator {
          display: none;
        }
      `}
    `,
    [p.disableClearIcon]
  );

/** Returns true if the item was a 'freeSolo' creation of the user.
 * Returns false if the item was selected from the provided list
 */
export const isCreatedOpt = <T,>(
  input: T | CreatedOpt
): input is CreatedOpt => (input as CreatedOpt)?.[CreatedOptSym];

type TType<TT, FreeS extends Opt<boolean>> =
  | TT
  | (FreeS extends true ? CreatedOpt : never);

type Props<
  F extends FieldValues,
  TT, // type of object in options array
  Mult extends Opt<boolean>, // multiple selections allowed?
  FreeS extends Opt<boolean> // custom text input allowed?
> = InputPropsFor<'AutocompleteInput', F, TT, Mult, FreeS>;

export default function FormAutocomplete<
  F extends FieldValues,
  TT = undefined,
  Mult extends Opt<boolean> = undefined,
  FreeS extends Opt<boolean> = undefined
>({
  controlName,
  optional,
  rules = {},
  control,
  errors,
  defaults = {},
  classicPlaceholder,
  renderOption,
  getOptionLabel,
  getOptionSelected = isEqual,
  freeSolo,
  multiple,
  disableClearIcon,
  TextInputProps,
  ...props
}: Props<F, TType<TT, FreeS>, Mult, FreeS>): JSX.Element {
  const controller: UseControllerOptions = {
    control: control as Control,
    name: controlName,
    defaultValue: defaults[controlName] || null,
    rules: {
      required: optional ? undefined : 'Required',
      ...rules,
    },
  };

  const filter = useMemo(
    () => createFilterOptions<TType<TT, FreeS>>(),
    []
  );

  const autocompleteCss = useAutocompleteCss({ disableClearIcon });

  const AutocompleteComponent = ({
    ref,
    value,
    onChange,
    name,
    ...controllerRenderProps
  }: Partial<ControllerRenderProps<F>> = {}) => (
    <Autocomplete
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value={value}
      css={autocompleteCss}
      onChange={(evt, selection) => onChange?.(selection)}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        if (params.inputValue !== '' && freeSolo) {
          // NOTE: needed little bit of type-magic to convince the compiler
          // that everything is indeed fine here.
          // 'filtered' is of type 'TType<TT, FreeS>', and since 'freeSolo'
          // is of type FreeS and in this if-block we know 'freeSolo' === true,
          // we can safely cast 'filtered' as 'TType<TT, true>' and back again.
          const freeFiltered = filtered as TType<TT, true>[];
          freeFiltered.push({
            inputValue: params.inputValue,
            title: `Add "${params.inputValue}"`,
            [CreatedOptSym]: true,
          });
          return freeFiltered as TType<TT, FreeS>[];
        }
        return filtered;
      }}
      renderInput={renderInputProps => (
        <AutocompleteTextInput
          $multiple={multiple}
          {...{ classicPlaceholder, errors, name, controlName }}
          {...renderInputProps}
          {...(_.isFunction(TextInputProps)
            ? TextInputProps(renderInputProps)
            : TextInputProps)}
        />
      )}
      renderOption={(opt, state) =>
        renderOption?.(opt, state) ||
        (isCreatedOpt(opt) && opt.title) ||
        getOptionLabel?.(opt)
      }
      getOptionLabel={
        getOptionLabel && (opt => (opt ? getOptionLabel(opt) : ''))
      }
      {...{ getOptionSelected, freeSolo, multiple, ref }}
      {...props}
      {...controllerRenderProps}
    />
  );

  return (
    <Controller render={AutocompleteComponent} {...controller} />
  );
}
